import { g } from "analytics";
import { unitVector } from "apparatus/draw";
import { stepLeq } from "apparatus/library/common";
import ArrowTool from "arrows/tool";
import { commandProcessor } from "command/command";
import { Move } from "editing/move";
import MultiTool from "editing/multitool";
import { PathEditTool } from "editing/pathedit/tool";
import { RotationOverlay } from "editing/rotation/rotation_overlay";
import { ZoomSlider } from "editing/zoom";
import { FreehandTool } from "freehand/tool";
import { focusMainApp, Keys, keyToDirection, registerKeyboardShortcutHandler } from "keyboard";
import { EditorCanvasComponent } from "layout/editor/editor_canvas";
import { SelectionOverlay } from "layout/editor/selection_overlay";
import * as React from "react";
import { ID } from "store/id";
import store from "store/store";
import { convertFromPaperToHTMLCoordinates } from "types";
import { PanTool } from "view/pan";
import { add } from "../../types";
/*

Zoom      25 ---  50 --- 100 --- 200 --- 300

Small     10       5       2      1       0.5
Medium    50      20      10      5       1
Large    200     100      50     25      15

*/
/** How much to multiply the distance by when moving by small increments. */
const moveByKeyDistanceSmall = (zoom) => stepLeq(zoom, [[0.33, 10], [0.75, 5], [1.5, 2], [2.5, 1]], 0.5);
/** How many pixels to move by when moving items using keyboard. */
const moveByKeyDistanceNormal = (zoom) => stepLeq(zoom, [[0.33, 50], [0.75, 20], [1.5, 10], [2.5, 5]], 1);
/** How much to multiply the distance by when moving by large increments. */
const moveByKeyDistanceLarge = (zoom) => stepLeq(zoom, [[0.33, 200], [0.75, 100], [1.5, 50], [2.5, 25]], 15);
/**
 * Container for the area that contains the editor, i.e.
 * the canvas, zoom slider, property panel but e.g.
 * not the sidebar.
 */
export class EditorContainer extends React.Component {
    constructor(props) {
        super(props);
        this.editorCanvasRef = React.createRef();
        this.rotationOverlayRef = React.createRef();
        this.selectionOverlayRef = React.createRef();
        this.state = {
            isInteracting: false,
            isEditingText: false,
            globalSnapViz: { lines: [] },
        };
    }
    componentDidMount() {
        let editorCanvas = this.editorCanvasRef.current;
        // Update zoom.
        this.setZoom(this.props.zoom);
        // Set up tools and activate.
        this.arrowTool = new ArrowTool(editorCanvas.layers, this),
            this.allTools = {
                select: new MultiTool(editorCanvas.layers, this),
                arrow: this.arrowTool,
                pan: new PanTool(this),
                freehand: new FreehandTool(this),
                pathedit: new PathEditTool(this)
            };
        // Activate tool.
        this.currentTool = this.allTools[this.props.tool];
        this.currentTool.activate();
        // Set up keyboard shortcuts.
        this.unregisterKeyboardShortcut = registerKeyboardShortcutHandler(e => {
            // Moving by key. Hold shift to make big jumps.
            if (e.which >= Keys.LEFT && e.which <= Keys.DOWN) {
                let direction = keyToDirection(e.which);
                let distanceFn = ((e.ctrlKey || e.metaKey) && e.shiftKey)
                    ? moveByKeyDistanceSmall
                    : (e.shiftKey
                        ? moveByKeyDistanceLarge
                        : moveByKeyDistanceNormal);
                let distance = distanceFn(this.props.zoom);
                this.moveByKey(direction, distance);
            }
        });
    }
    componentWillUnmount() {
        if (this.unregisterKeyboardShortcut) {
            this.unregisterKeyboardShortcut();
        }
    }
    // TODO: Clean this up, this is needed to compute the selection
    // box correctly but does not grant access to arrows.
    getApparatusComponent(id) {
        return this.editorCanvasRef.current.getApparatusComponent(id);
    }
    getObjectComponent(id) {
        return this.editorCanvasRef.current.getObjectComponent(id);
    }
    setArrow(data) {
        this.editorCanvasRef.current.setState({
            arrow: data
        });
    }
    setRenderArea(delta) {
        var _a;
        (_a = this.editorCanvasRef.current.renderAreaRef.current) === null || _a === void 0 ? void 0 : _a.moveByDelta(delta);
    }
    setGlobalSnapViz(viz) {
        var _a;
        (_a = this.editorCanvasRef.current.globalSnapVizViewRef.current) === null || _a === void 0 ? void 0 : _a.setViz(viz);
    }
    componentDidUpdate(prevProps) {
        // Activate current tool, if changed.
        if (prevProps.tool != this.props.tool) {
            this.currentTool = this.allTools[this.props.tool];
            this.currentTool.activate();
        }
        // Update the selection and rotation overlays.
        let selectionOverlay = this.selectionOverlayRef.current;
        let selectedItems = this.props.selected.items;
        let selectionMetrics = this.editorCanvasRef.current.computeSelectionMetrics();
        // Notify rotation handler of components corresponding to currently selected items.
        selectionOverlay.rotationHandler.selection = selectionMetrics;
        selectionOverlay.rotationHandler.selectedApparatus = selectedItems.map(item => this.getApparatusComponent(item.id)).filter((a) => a !== null);
        selectionOverlay.rotationHandler.selectedArrows = this.props.selected.arrows.map(a => (this.getObjectComponent(a.id)));
        // Convert from Paper to HTML coordinates. These are used by the
        // overlay components that are in HTML DOM rather than canvas.
        let convertedMetrics = convertFromPaperToHTMLCoordinates(selectionMetrics);
        // Update state on the selection overlay.
        this.selectionOverlayRef.current.setState({
            isVisible: !this.props.selected.isEmpty,
            center: convertedMetrics.pivot,
            bounds: convertedMetrics.bounds,
        });
        // Update state on the rotation overlay.
        this.rotationOverlayRef.current.setState({
            pivot: convertedMetrics.pivot
        });
    }
    render() {
        // Resolve final interaction state from multiple states.
        let isInteracting = this.state.isInteracting;
        if (!isInteracting && this.state.isEditingText) {
            isInteracting = "general";
        }
        return React.createElement("div", { id: "editor-container", key: "editorContainer" },
            React.createElement(EditorCanvasComponent, Object.assign({}, this.props, { key: "editor_canvas", tool: this.props.tool, ref: this.editorCanvasRef, onPaperReady: this.props.onPaperReady, isInteracting: isInteracting })),
            React.createElement(RotationOverlay, { key: "rotationOverlay", ref: this.rotationOverlayRef }),
            React.createElement(SelectionOverlay, { key: "selectionOverlay", ref: this.selectionOverlayRef, hideDueToInteraction: isInteracting !== false, onStartRotation: t => this.onStartRotation(t), onUpdateRotation: a => this.onUpdateRotation(a), onEndRotation: () => this.onEndRotation() }),
            React.createElement(ZoomSlider, { key: "zoomSlider", zoom: this.props.zoom, onStartZoom: () => this.onStartZoom(), onUpdateZoom: z => this.onUpdateZoom(z), onEndZoom: () => this.onEndZoom() }));
    }
    // Temporarily sets the zoom to this value.
    setZoom(zoom) {
        let canvasEditor = this.editorCanvasRef.current;
        if (canvasEditor) {
            canvasEditor.setZoom(zoom);
        }
    }
    onStartRotation(type) {
        this.setIsInteracting(true);
        this.rotationOverlayRef.current.setState({
            type: type,
            angle: 0
        });
    }
    onUpdateRotation(angle) {
        this.rotationOverlayRef.current.setState({ angle: angle });
    }
    onEndRotation() {
        this.setIsInteracting(false);
        this.rotationOverlayRef.current.setState({ type: undefined });
        focusMainApp();
    }
    // Zoom.
    onStartZoom() {
        this.setState({ isInteracting: "zoom" });
    }
    onUpdateZoom(zoom) {
        this.editorCanvasRef.current.setZoom(zoom);
    }
    onEndZoom() {
        this.setState({ isInteracting: false });
    }
    setIsInteracting(isInteracting) {
        this.setState({ isInteracting: isInteracting ? "general" : false });
    }
    setIsMovingItems(isMoving) {
        this.setState({ isInteracting: isMoving ? "move" : false });
    }
    setIsEditingText(value) {
        this.setState({ isEditingText: value });
    }
    moveByKey(direction, distance) {
        let selectedItemIds = store.selectedObjectsID();
        if (selectedItemIds.length == 0)
            return;
        g("MoveByKey");
        let delta = unitVector(direction).multiply(distance);
        commandProcessor.executeReplacingLastCommand((lastCommand) => {
            if (!(lastCommand instanceof Move))
                return false;
            if (!ID.equals(lastCommand.ids, selectedItemIds))
                return false;
            const combinedDelta = add(delta, lastCommand.delta);
            return new Move(selectedItemIds, combinedDelta);
        }, new Move(selectedItemIds, delta));
    }
    /** Exports current canvas contents to various image formats. */
    exportImages(options) {
        return this.editorCanvasRef.current.export(options);
    }
    exportThumbnail() {
        return this.editorCanvasRef.current.exportThumbnail();
    }
    setArrowTemplate(data) {
        this.arrowTool.updateTemplateArrow(data);
    }
    getProjectBounds() {
        return this.editorCanvasRef.current.getProjectBounds();
    }
}
